/**
 * BookView — shared layout shell for comic/book viewing.
 *
 * Dark theme with header (back, title, page indicator, actions),
 * side prev/next navigation, and a content area that shows either
 * page images or a custom overlay (e.g. generation skeleton).
 *
 * Used by:
 *   - StoryBuilder (book tab) — passes pages from comicStore + generation overlay
 *   - ComicViewer (gallery) — passes pages loaded from server
 */

import React, { useRef, useCallback, useEffect, useMemo, useState } from "react";
import { trackButton, Button, View as MetricView } from "../../services/metrics";
import {
  View,
  Text,
  Image,
  Pressable,
  FlatList,
  Animated,
  ActivityIndicator,
  BackHandler,
  StyleSheet,
  Platform,
  useWindowDimensions,
  type ListRenderItemInfo,
  type ViewToken,
} from "react-native";

import { useVoiceOver } from "../../audio/useVoiceOver";
import { useSubtitles } from "../../audio/useSubtitles";
import { usePageImage } from "../../hooks/usePageImage";
import type { SpeechMark } from "@amzn/a4k-creator-studio-shared";

const caretLeftIcon = require("../../assets/icons/caret-left.png");
const playIcon = require("../../assets/icons/play.png");
const sparkleIcon = require("../../assets/icons/sparkle.png");
const shareIcon = require("../../assets/icons/share.png");
const alertCircleIcon = require("../../assets/icons/alert-circle.png");

import BookGenerating from "./BookGenerating";
import { useWebDragScroll } from "../../lib/useWebSwipe";
import { useKeepAwake } from "../../hooks/useKeepAwake";
import { backButtonStyle, colors, textStyle } from "../../styles";
const BookDebugOverlay = __DEV__ ? require("./BookDebugOverlay").default : null;

const DARK_BG = colors.surfaceDark;
const DARK_TEXT = colors.galleryOnSurface;
const BTN_BG = colors.btnSecondary;
const BTN_ICON_COLOR = colors.white;

export interface BookPage {
  imageId?: string;
  imageBase64?: string;
  imageUrl?: string;
  /** Image generation was blocked by a content check — render an "oops" placeholder. */
  imageError?: boolean;
  pageNumber: number;
  isCover?: boolean;
  narrativeText?: string;
  audioBase64?: string;
  speechMarks?: SpeechMark[];
}

interface Props {
  pages: BookPage[];
  currentPage: number;
  onPageChange: (index: number) => void;
  title?: string;
  onBack: () => void;
  onRedraw?: () => void;
  onShare?: () => void;
  actionsDisabled?: boolean;
  /** Show a loading indicator in the content area (e.g. loading from gallery) */
  loading?: boolean;
  /** Status text shown at the bottom during generation. Fades out when removed. */
  statusText?: string;
  /** Enable voice-over narration with auto-advance */
  voiceOver?: boolean;
  /** Whether the total page count is final. While layout is still streaming in,
   *  pages.length is a partial count, so the indicator omits the "of N" total. */
  totalKnown?: boolean;
  /** Authoritative final page count (cover included, same basis as pages.length).
   *  Preferred over pages.length for the "of N" total so a dropped page event
   *  can't skew it. Falls back to pages.length when omitted (e.g. gallery). */
  totalPages?: number | null;
}

function PageContent({
  page,
  fullscreen,
  horizontalInset,
  contentHeight,
  onTap,
}: {
  page: BookPage;
  fullscreen: boolean;
  horizontalInset: number;
  contentHeight: number | undefined;
  onTap: () => void;
}) {
  const uri = usePageImage(page);
  if (uri) {
    return (
      <Pressable onPress={onTap} style={[s.pageImageWrap, { marginHorizontal: horizontalInset }]}>
        {Platform.OS === "web" ? (
          <img src={uri} alt={`Comic page ${page.pageNumber}`} draggable={false} style={{ maxWidth: "100%", maxHeight: contentHeight, objectFit: "contain", borderRadius: fullscreen ? 0 : 8, pointerEvents: "none" } as any} />
        ) : (
          <Image source={{ uri }} style={[s.pageImage, fullscreen && { borderRadius: 0 }]} resizeMode="contain" resizeMethod="resize" />
        )}
      </Pressable>
    );
  }
  if (page.imageError) {
    return (
      <Pressable onPress={onTap} style={[s.pageImageWrap, { marginHorizontal: horizontalInset }]}>
        <View style={s.errorPage}>
          <Image source={alertCircleIcon} style={s.errorIcon} resizeMode="contain" />
          <Text style={s.errorTitle}>Oops! This page didn't come out right.</Text>
          <Text style={s.errorBody}>We couldn't draw this one, but your story keeps going — turn the page!</Text>
        </View>
      </Pressable>
    );
  }
  return (
    <Pressable onPress={onTap} style={{ flex: 1 }}>
      <BookGenerating />
    </Pressable>
  );
}

export default function BookView({
  pages,
  currentPage,
  onPageChange,
  title,
  onBack,
  onRedraw,
  onShare,
  actionsDisabled,
  loading,
  statusText,
  voiceOver = false,
  totalKnown = true,
  totalPages,
}: Props) {
  const { width, height: windowHeight } = useWindowDimensions();
  const pageCount = pages.length;
  const [audioEnabled, setAudioEnabled] = useState(false);
  useKeepAwake('book-view');

  const voiceOverPageChange = useCallback((page: number) => {
    programmaticRef.current = null; // Clear stale programmatic state before new advance
    isProgrammaticRef.current = true;
    onPageChange(page);
  }, [onPageChange]);

  const { isSpeaking, isPaused } = useVoiceOver({
    pages,
    currentPage,
    onPageChange: voiceOverPageChange,
    enabled: voiceOver && audioEnabled && !loading && pageCount > 0,
    title,
  });

  const currentPageMarks = pages[currentPage]?.speechMarks || [];
  const subtitle = useSubtitles({
    speechMarks: currentPageMarks,
    isPlaying: isSpeaking,
    isPaused,
  });

  const toggleAudio = useCallback(() => {
    trackButton(MetricView.BookView, Button.BookView.ReadAloud);
    setAudioEnabled((prev) => !prev);
  }, []);

  const navBtnSize = width * 0.0533;
  const navBtnRadius = navBtnSize * 0.4;
  const navBtnMargin = width * 0.032;
  const navIconSize = navBtnSize * 0.4;

  // --- FlatList paging ---
  const flatListRef = useRef<FlatList>(null);
  const programmaticRef = useRef<number | null>(null);
  const isProgrammaticRef = useRef(false);

  useEffect(() => {
    if (flatListRef.current && pageCount > 0 && isProgrammaticRef.current) {
      isProgrammaticRef.current = false;
      programmaticRef.current = currentPage;
      flatListRef.current.scrollToIndex({ index: currentPage, animated: true });
    }
  }, [currentPage, pageCount]);

  useEffect(() => {
    if (flatListRef.current && pageCount > 0) {
      flatListRef.current.scrollToIndex({ index: currentPage, animated: false });
    }
  }, [width]);

  // Keyboard nav (web)
  useEffect(() => {
    if (Platform.OS !== "web") return;
    const handler = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable) return;
      if (e.key === "ArrowLeft" && currentPage > 0) {
        isProgrammaticRef.current = true;
        onPageChange(currentPage - 1);
      } else if (e.key === "ArrowRight" && currentPage < pageCount - 1) {
        isProgrammaticRef.current = true;
        onPageChange(currentPage + 1);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [currentPage, pageCount, onPageChange]);

  // --- Fullscreen ---
  const [fullscreen, setFullscreen] = useState(false);
  const scaleAnim = useRef(new Animated.Value(1)).current;

  // Android back button
  useEffect(() => {
    if (Platform.OS === "web") return;
    const sub = BackHandler.addEventListener("hardwareBackPress", () => {
      if (fullscreen) {
        setFullscreen(false);
        Animated.sequence([
          Animated.timing(scaleAnim, { toValue: 0.92, duration: 150, useNativeDriver: true }),
          Animated.timing(scaleAnim, { toValue: 1, duration: 150, useNativeDriver: true }),
        ]).start();
      } else {
        onBack();
      }
      return true;
    });
    return () => sub.remove();
  }, [onBack, fullscreen, scaleAnim]);

  const onPageChangeRef = useRef(onPageChange);
  onPageChangeRef.current = onPageChange;

  const onViewableItemsChanged = useRef(
    ({ viewableItems }: { viewableItems: ViewToken[] }) => {
      if (viewableItems.length > 0 && viewableItems[0].index != null) {
        const idx = viewableItems[0].index;
        if (programmaticRef.current != null) {
          if (idx === programmaticRef.current) programmaticRef.current = null;
          return;
        }
        onPageChangeRef.current(idx);
      }
    },
  ).current;

  const viewabilityConfig = useRef({ viewAreaCoveragePercentThreshold: 50 }).current;

  const goNext = useCallback(() => {
    if (currentPage < pageCount - 1) {
      isProgrammaticRef.current = true;
      onPageChange(currentPage + 1);
    }
  }, [currentPage, pageCount, onPageChange]);

  const goPrev = useCallback(() => {
    if (currentPage > 0) {
      isProgrammaticRef.current = true;
      onPageChange(currentPage - 1);
    }
  }, [currentPage, onPageChange]);

  const dragContainerRef = useWebDragScroll<View>({
    pageWidth: width,
    currentPage,
    pageCount,
    onPageChange: (page: number) => { isProgrammaticRef.current = true; onPageChange(page); },
  });

  const prevOpacity = useRef(new Animated.Value(currentPage > 0 ? 1 : 0)).current;
  const nextOpacity = useRef(new Animated.Value(currentPage < pageCount - 1 ? 1 : 0)).current;

  useEffect(() => {
    Animated.timing(prevOpacity, { toValue: currentPage > 0 ? 1 : 0, duration: 200, useNativeDriver: true }).start();
    Animated.timing(nextOpacity, { toValue: currentPage < pageCount - 1 ? 1 : 0, duration: 200, useNativeDriver: true }).start();
  }, [currentPage, pageCount]);

  // --- Page indicator ---
  const pageLabel = useMemo(() => {
    if (pageCount === 0) return "";
    const hasCover = pages[0]?.isCover;
    if (hasCover && currentPage === 0) return "Cover";
    const offset = hasCover ? 1 : 0;
    const pageNum = currentPage - offset + 1;
    // While layout is still streaming, the total is partial — omit the "of N"
    // until it's final so we don't show a count that keeps changing. Once known,
    // prefer the authoritative totalPages over pages.length (which a dropped
    // page event can leave short), falling back to pages.length when not given.
    if (!totalKnown) return `Page ${pageNum}`;
    const total = (totalPages ?? pageCount) - offset;
    return `Page ${pageNum} of ${total}`;
  }, [currentPage, pageCount, pages, totalKnown, totalPages]);

  const lastTapRef = useRef(0);
  const handlePageTap = useCallback(() => {
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      // Double tap — fullscreen toggle
      setFullscreen((prev) => {
        const goingFullscreen = !prev;
        Animated.sequence([
          Animated.timing(scaleAnim, { toValue: goingFullscreen ? 1.08 : 0.92, duration: 150, useNativeDriver: true }),
          Animated.timing(scaleAnim, { toValue: 1, duration: 150, useNativeDriver: true }),
        ]).start();
        return goingFullscreen;
      });
    }
    lastTapRef.current = now;
  }, [scaleAnim]);

  // --- Render page ---
  const horizontalInset = fullscreen ? 0 : navBtnSize + navBtnMargin * 2;
  const contentHeight = Platform.OS === "web" ? (fullscreen ? windowHeight : windowHeight - 96) : undefined;
  const renderItem = useCallback(
    ({ item }: ListRenderItemInfo<number>) => {
      const page = pages[item];
      if (!page) return <View style={{ width, flex: 1 }} />;
      return (
        <View style={[s.pageSlot, { width, height: contentHeight }]}>
          <PageContent
            page={page}
            fullscreen={fullscreen}
            horizontalInset={horizontalInset}
            contentHeight={contentHeight}
            onTap={handlePageTap}
          />
        </View>
      );
    },
    [pages, width, contentHeight, handlePageTap, fullscreen, horizontalInset],
  );

  const data = useMemo(() => Array.from({ length: pageCount }, (_, i) => i), [pageCount]);
  const extraData = useMemo(() => ({ pages, fullscreen }), [pages, fullscreen]);
  const getItemLayout = useCallback((_: unknown, index: number) => ({ length: width, offset: width * index, index }), [width]);

  const showActions = onRedraw || onShare || (voiceOver && pages.some((p) => p.audioBase64));

  return (
    <View style={s.root}>
      {/* Header */}
      <View style={[s.header, { paddingHorizontal: navBtnMargin }]}>
        <Pressable style={s.backBtn} onPress={onBack} accessibilityLabel="Back">
          <Image source={caretLeftIcon} style={s.iconSm} resizeMode="contain" />
        </Pressable>

        <View style={s.titleArea}>
          <Text style={s.title} numberOfLines={1}>{title || "My Story"}</Text>
          {pageCount > 0 && <Text style={s.subtitle}>{pageLabel}</Text>}
        </View>

        {showActions && (
          <View style={s.actions}>
            {onRedraw && (
              <Pressable
                style={[s.actionBtn, actionsDisabled && s.actionDisabled]}
                onPress={() => { trackButton(MetricView.BookView, Button.BookView.Redraw); onRedraw(); }}
                disabled={actionsDisabled}
              >
                <Image source={sparkleIcon} style={s.actionIconImg} resizeMode="contain" />
                <Text style={s.actionLabel}>Redraw</Text>
              </Pressable>
            )}
            {voiceOver && pages.some((p) => p.audioBase64) && (
              <Pressable style={s.actionBtn} onPress={toggleAudio} accessibilityLabel={audioEnabled ? "Stop narration" : "Read Aloud"}>
                <Image source={audioEnabled ? require("../../assets/icons/ic-stop.png") : require("../../assets/icons/ic-play.png")} style={s.actionIconImg} resizeMode="contain" />
                <Text style={s.actionLabel}>{audioEnabled ? "Stop" : "Read Aloud"}</Text>
              </Pressable>
            )}
            {onShare && (
              <Pressable
                style={[s.actionBtn, actionsDisabled && s.actionDisabled]}
                onPress={onShare}
                disabled={actionsDisabled}
              >
                <Image source={shareIcon} style={s.actionIconImg} resizeMode="contain" />
                <Text style={s.actionLabel}>Share</Text>
              </Pressable>
            )}
          </View>
        )}
      </View>

      {/* Content area */}
      <Animated.View style={[s.content, fullscreen && s.contentFullscreen]}>

        {loading ? (
          <View style={s.loadingContainer}>
            <ActivityIndicator size="large" color={DARK_TEXT} />
            <Text style={s.loadingText}>Opening your story...</Text>
          </View>
        ) : pageCount > 0 ? (
          <>
            <Animated.View ref={dragContainerRef as any} style={{ flex: 1, transform: [{ scale: scaleAnim }] }}>
              <FlatList
                ref={flatListRef}
                testID="book-page-list"
                data={data}
                extraData={extraData}
                renderItem={renderItem}
                keyExtractor={String}
                horizontal
                pagingEnabled
                showsHorizontalScrollIndicator={false}
                bounces
                getItemLayout={getItemLayout}
                onViewableItemsChanged={onViewableItemsChanged}
                viewabilityConfig={viewabilityConfig}
                initialScrollIndex={currentPage}
                onScrollEndDrag={() => { programmaticRef.current = null; }}
                onMomentumScrollEnd={() => { programmaticRef.current = null; }}
                decelerationRate="fast"
                windowSize={3}
                maxToRenderPerBatch={1}
                initialNumToRender={1}
                removeClippedSubviews
              />
            </Animated.View>
            {!fullscreen && (
              <>
                <Animated.View style={[s.navBtn, { left: navBtnMargin, width: navBtnSize, height: navBtnSize, borderRadius: navBtnRadius, transform: [{ translateY: -navBtnSize / 2 }], opacity: prevOpacity }]} pointerEvents={currentPage > 0 ? "auto" : "none"}>
                  <Pressable onPress={goPrev} accessibilityLabel="Previous page" style={s.navBtnInner}>
                    <Image source={playIcon} style={{ width: navIconSize, height: navIconSize, tintColor: BTN_ICON_COLOR, transform: [{ rotate: "180deg" }] }} resizeMode="contain" />
                  </Pressable>
                </Animated.View>
                <Animated.View style={[s.navBtn, { right: navBtnMargin, width: navBtnSize, height: navBtnSize, borderRadius: navBtnRadius, transform: [{ translateY: -navBtnSize / 2 }], opacity: nextOpacity }]} pointerEvents={currentPage < pageCount - 1 ? "auto" : "none"}>
                  <Pressable onPress={goNext} accessibilityLabel="Next page" style={s.navBtnInner}>
                    <Image source={playIcon} style={{ width: navIconSize, height: navIconSize, tintColor: BTN_ICON_COLOR }} resizeMode="contain" />
                  </Pressable>
                </Animated.View>
              </>
            )}
          </>
        ) : (
          <BookGenerating />
        )}
      </Animated.View>

      {/* Subtitle overlay — phrase-level highlight, one sentence at a time */}
      {voiceOver && audioEnabled && isSpeaking && subtitle.visible && (
        <View style={[s.subtitleScrim, fullscreen && s.subtitleScrimFullscreen]} pointerEvents="none">
          <Text style={[s.subtitleText, fullscreen && s.subtitleTextFullscreen]}>
            {subtitle.words.map((word, i) => {
              const inPhrase = i >= subtitle.phraseStart && i < subtitle.phraseEnd;
              const spoken = i < subtitle.phraseStart;
              return (
                <Text key={i}>
                  <Text style={inPhrase ? s.subtitlePhraseActive : spoken ? s.subtitleWordSpoken : s.subtitleWordUpcoming}>
                    {word}
                  </Text>
                  <Text style={s.subtitleWordUpcoming}>{" "}</Text>
                </Text>
              );
            })}
          </Text>
        </View>
      )}

      {/* Status text at bottom during generation */}
      {statusText && (
        <View style={s.statusBar}>
          <ActivityIndicator size="small" color={DARK_TEXT} />
          <Text style={s.statusBarText}>{statusText}</Text>
        </View>
      )}

      {/* Fullscreen backdrop */}
      {__DEV__ && BookDebugOverlay && <BookDebugOverlay />}
    </View>
  );
}

const s = StyleSheet.create({
  root: { flex: 1, backgroundColor: DARK_BG },
  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 32,
    gap: 48,
  },
  backBtn: backButtonStyle.container,
  iconSm: backButtonStyle.icon,
  titleArea: { flex: 1 },
  title: { color: DARK_TEXT, fontSize: 28, ...textStyle.headingMedium },
  subtitle: { color: DARK_TEXT, fontSize: 16, ...textStyle.headingMedium, marginTop: 2 },
  actions: { flexDirection: "row", alignItems: "center", gap: 12 },
  actionBtn: {
    flexDirection: "row", alignItems: "center", gap: 8,
    paddingHorizontal: 20, height: 40, borderRadius: 9999,
    backgroundColor: BTN_BG,
  },
  actionDisabled: { opacity: 0.3 },
  actionIconImg: { width: 20, height: 20, tintColor: BTN_ICON_COLOR },
  actionLabel: { color: colors.white, fontSize: 18, ...textStyle.headingMedium },
  content: { flex: 1, position: "relative" },
  contentFullscreen: {
    position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
    zIndex: 999, backgroundColor: DARK_BG,
  },
  loadingContainer: { flex: 1, alignItems: "center", justifyContent: "center" },
  loadingText: { color: DARK_TEXT, fontSize: 16, marginTop: 12 },
  subtitleScrim: {
    position: "absolute", bottom: 48, left: 40, right: 40,
    maxHeight: 80,
    paddingVertical: 10, paddingHorizontal: 18,
    backgroundColor: "rgba(0, 0, 0, 0.72)",
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    overflow: "hidden",
  },
  subtitleScrimFullscreen: {
    bottom: 24, left: 32, right: 32,
    maxHeight: 90,
  },
  subtitleText: {
    fontSize: 20, lineHeight: 30, textAlign: "center",
    textShadowColor: "rgba(0, 0, 0, 0.8)",
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 3,
  },
  subtitleTextFullscreen: {
    fontSize: 24, lineHeight: 34,
  },
  subtitlePhraseActive: {
    color: "#FFFFFF", fontWeight: "700",
    textShadowColor: "rgba(0, 0, 0, 0.9)",
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 4,
  },
  subtitleWordSpoken: { color: "rgba(255, 255, 255, 0.65)", fontWeight: "400" },
  subtitleWordUpcoming: { color: "rgba(255, 255, 255, 0.3)" },
  statusBar: {
    flexDirection: "row", alignItems: "center", gap: 9,
    paddingHorizontal: 64, paddingVertical: 16,
  },
  statusBarText: { color: DARK_TEXT, fontSize: 16 },
  pageSlot: { flex: 1, alignItems: "stretch", justifyContent: "center", overflow: "hidden" },
  pageImageWrap: { flex: 1, alignItems: "center", justifyContent: "center" },
  errorPage: {
    flex: 1, alignSelf: "stretch",
    alignItems: "center", justifyContent: "center",
    paddingHorizontal: 48,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: "rgba(255,255,255,0.2)",
    backgroundColor: "rgba(255,255,255,0.06)",
  },
  errorIcon: { width: 56, height: 56, marginBottom: 20, opacity: 0.6, tintColor: DARK_TEXT },
  errorTitle: { color: DARK_TEXT, fontSize: 22, textAlign: "center", ...textStyle.headingMedium },
  errorBody: { color: DARK_TEXT, fontSize: 16, textAlign: "center", marginTop: 10, opacity: 0.75, maxWidth: 420 },
  pageImage: { width: "100%", height: "100%", borderRadius: 8 },
  navBtn: {
    position: "absolute", top: "50%",
    backgroundColor: BTN_BG,
    alignItems: "center", justifyContent: "center",
    shadowColor: "#000", shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.2, shadowRadius: 4,
    elevation: 4,
  },
  navBtnInner: { width: "100%", height: "100%", alignItems: "center", justifyContent: "center" },
});
